import type { DataTableSort } from '../components/primitives';
import { sortRows } from '../table/sort-rows';
import { useTableSort } from './useTableSort';
import { LIST_QUERY_KEYS, useUrlPage, useUrlSearch } from './useListQueryState';

/** Rows per page when a table does not say otherwise. */
export const DEFAULT_PAGE_SIZE = 25;

export interface UseClientTableOptions<Row> {
  readonly rows: readonly Row[];
  /** The text a search matches against, e.g. name and email joined. */
  readonly searchText: (row: Row) => string;
  /** Row fields the headers may sort by. */
  readonly sortKeys: readonly string[];
  readonly defaultSort?: DataTableSort | undefined;
  readonly pageSize?: number;
  /**
   * Distinguishes this table's URL keys from another table's on the same page:
   * `?<prefix>q=&<prefix>sort=&<prefix>dir=&<prefix>page=`. Omit for a page's
   * only table.
   */
  readonly prefix?: string;
}

export interface ClientTable<Row> {
  /** The live search box value and its setter. */
  readonly search: string;
  readonly setSearch: (value: string) => void;
  readonly sort: DataTableSort | undefined;
  readonly onSort: (key: string) => void;
  /** The rows to render: searched, sorted and cut to the current page. */
  readonly pageRows: readonly Row[];
  /** Rows that match the search, across every page. */
  readonly matched: number;
  readonly page: number;
  readonly totalPages: number;
  readonly setPage: (page: number) => void;
  /** "1–25 of 312" for the pager. */
  readonly summary: string;
}

/**
 * Search, sort and pagination for a table whose rows are all loaded, with each
 * kept in the URL (`?q=`, `?sort=&dir=`, `?page=`) so a filtered view survives
 * a reload and can be shared. Every portal table is searchable, sortable and
 * paginated (design-system rule, 2026-09-22); a server-paged list uses
 * `useUrlSearch`, `useTableSort` and `useUrlPage` directly instead.
 *
 * @param options - The rows, what search matches, the sortable keys and the page size.
 * @returns What the search box, `DataTable` and `Pagination` need.
 */
export function useClientTable<Row>(options: UseClientTableOptions<Row>): ClientTable<Row> {
  const pageSize = options.pageSize ?? DEFAULT_PAGE_SIZE;
  const prefix = options.prefix ?? '';
  const pageKey = `${prefix}${LIST_QUERY_KEYS.page}`;
  const { search, setSearch, debounced } = useUrlSearch(`${prefix}${LIST_QUERY_KEYS.search}`, pageKey);
  const { sort, onSort } = useTableSort(options.sortKeys, { defaultSort: options.defaultSort, prefix });

  const needle = debounced.toLowerCase();
  const matching =
    needle === ''
      ? options.rows
      : options.rows.filter((row) => options.searchText(row).toLowerCase().includes(needle));
  const sorted = sortRows(matching, sort);
  const paged = usePagedRows(sorted, { pageKey, pageSize });

  return {
    search,
    setSearch,
    sort,
    onSort,
    pageRows: paged.pageRows,
    matched: sorted.length,
    page: paged.page,
    totalPages: paged.totalPages,
    setPage: paged.setPage,
    summary: paged.summary,
  };
}

/** One page of an already filtered and sorted list, with the page bound to the URL. */
export interface PagedRows<Row> {
  readonly pageRows: readonly Row[];
  readonly page: number;
  readonly totalPages: number;
  readonly setPage: (page: number) => void;
  /** "1–25 of 140", or "No matches". */
  readonly summary: string;
}

/**
 * Pages a list a table already searched and sorted itself (e.g. with
 * `filterRows` + `sortRows`), keeping the page in the URL. Pair the page key
 * with the table's `useUrlSearch(..., pageKey)` so a new search starts on page 1.
 *
 * @param rows - The filtered, sorted rows.
 * @param options - `pageKey` (default `page`; give each table on a screen its own) and `pageSize` (default 25).
 * @returns The current page's rows and the pager state for `<Pagination>`.
 */
export function usePagedRows<Row>(
  rows: readonly Row[],
  options: { readonly pageKey?: string; readonly pageSize?: number } = {},
): PagedRows<Row> {
  const pageSize = options.pageSize ?? DEFAULT_PAGE_SIZE;
  const { page: requestedPage, setPage } = useUrlPage(options.pageKey ?? LIST_QUERY_KEYS.page);
  const totalPages = Math.max(1, Math.ceil(rows.length / pageSize));
  // A narrowed list never opens on a page that no longer exists.
  const page = Math.min(requestedPage, totalPages);
  const start = (page - 1) * pageSize;
  const pageRows = rows.slice(start, start + pageSize);
  return {
    pageRows,
    page,
    totalPages,
    setPage,
    summary:
      rows.length === 0
        ? 'No matches'
        : `${(start + 1).toLocaleString('en-US')}–${(start + pageRows.length).toLocaleString('en-US')} of ${rows.length.toLocaleString('en-US')}`,
  };
}
